import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import { escapeRegExp } from "lodash";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  DirectionEnum,
  formatHubSettingsDirectionLabel,
  IHubStationEdgePair,
  IHubWithStationPairs,
  useDrawers,
  useHasPermissions,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { InputSearch } from "../../../../common/input-fields/search";
import { FramerTooltip } from "../../../../common/tooltips";
import { decorateWithMark } from "../../../../grid/generic/table/table-defs/helpers/decorateWithMark";
import {
  HubStationRouteTooltipContent,
  HubStationsInfoContentStyled,
} from "./hub-stations-info.content.styled";
import { loadHubsWithStationPairs } from "./hub-stations-info.content.utils";

const includesSearch = (value: string | undefined, search: string): boolean =>
  Boolean(value?.toLowerCase().includes(search));

const decorateWithSearch = (value: string, searchValue: string): ReactNode => {
  const search = searchValue.trim();
  if (!search) {
    return value;
  }

  if (!value.toLowerCase().match(escapeRegExp(search).toLowerCase())) {
    return value;
  }

  return decorateWithMark(value, search);
};

const filterHubsBySearch = (
  hubs: IHubWithStationPairs[],
  searchRaw: string,
): IHubWithStationPairs[] => {
  const search = searchRaw.trim().toLowerCase();
  if (!search) {
    return hubs;
  }

  return hubs
    .map(hub => {
      const hubMatches =
        includesSearch(hub.hubName, search) ||
        includesSearch(hub.hubAreaName, search);

      const pairs = hubMatches
        ? hub.pairs
        : hub.pairs.filter(
            (pair: IHubStationEdgePair) =>
              includesSearch(pair.stationName, search) ||
              includesSearch(pair.pairedStationName, search) ||
              includesSearch(
                formatHubSettingsDirectionLabel(pair.direction),
                search,
              ),
          );

      return { ...hub, pairs };
    })
    .filter(hub => hub.pairs.length > 0);
};

const formatTooltipLabel = (name: string, id?: number): string =>
  id ? `${name} (${id})` : name;

interface IStationBadgeProps {
  name: string;
  areaName?: string;
  areaId?: number;
  stationId: number;
  isHub?: boolean;
  canOpen: boolean;
  searchValue: string;
  onOpen: (stationId: number) => void;
}

const StationBadge: FC<IStationBadgeProps> = ({
  name,
  areaName,
  areaId,
  stationId,
  isHub,
  canOpen,
  searchValue,
  onOpen,
}) => {
  const highlightedName = decorateWithSearch(name, searchValue);
  const tooltipContent = (
    <HubStationRouteTooltipContent>
      <div className="hub-info-station-tooltip__name">
        {formatTooltipLabel(name, stationId)}
      </div>
      {areaName ? (
        <div className="hub-info-station-tooltip__area">
          {formatTooltipLabel(areaName, areaId)}
        </div>
      ) : null}
    </HubStationRouteTooltipContent>
  );

  const badge = canOpen ? (
    <button
      type="button"
      className={clsx(
        "hub-info-station-badge",
        "hub-info-station-badge--clickable",
        isHub && "hub-info-station-badge--hub",
      )}
      onClick={() => onOpen(stationId)}
    >
      <span className="hub-info-station-name">{highlightedName}</span>
    </button>
  ) : (
    <span
      className={clsx(
        "hub-info-station-badge",
        isHub && "hub-info-station-badge--hub",
      )}
    >
      <span className="hub-info-station-name">{highlightedName}</span>
    </span>
  );

  return <FramerTooltip content={tooltipContent}>{badge}</FramerTooltip>;
};

export const HubStationsInfoContent: FC = () => {
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { hasPermissionViewStation } = useHaveCustomPermissions();
  const { hasPermissions: hasPermissionViewArea } = useHasPermissions({
    entity: "area",
    action: "read",
  });
  const [hubs, setHubs] = useState<IHubWithStationPairs[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const canOpenStation = Boolean(hasPermissionViewStation);
  const canOpenArea = Boolean(hasPermissionViewArea);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    void loadHubsWithStationPairs({
      isCancelled: () => cancelled,
      setHubs,
      setIsLoading,
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredHubs = useMemo(
    () => filterHubsBySearch(hubs, searchValue),
    [hubs, searchValue],
  );

  const handleOpenStation = useEvent((stationId: number) => {
    if (!canOpenStation || !stationId) {
      return;
    }

    openEditFormDrawer({
      entity: "station",
      id: stationId,
    });
  });

  const handleOpenArea = useEvent((areaId: number) => {
    if (!canOpenArea || !areaId) {
      return;
    }

    openEditFormDrawer({
      entity: "area",
      id: areaId,
    });
  });

  const renderHubName = (name: string): ReactNode =>
    decorateWithSearch(name, searchValue);

  return (
    <HubStationsInfoContentStyled>
      {isLoading && (
        <div className="hub-info-loading">
          <CircularProgress size={28} />
        </div>
      )}

      {!isLoading && !hubs.length && (
        <div className="hub-info-empty">{t("hub.noHubAvailable")}</div>
      )}

      {!isLoading && !!hubs.length && (
        <>
          <div className="hub-info-search">
            <InputSearch
              label={t("general.SEARCH")}
              value={searchValue}
              onSearchType={setSearchValue}
              onSearchApply={setSearchValue}
            />
          </div>

          <div className="hub-info-columns-header">
            <span className="hub-info-col-hub">
              {t("hub.hubStationsColumn", { defaultValue: "Hub stations" })}
            </span>
            <span className="hub-info-col-route">
              <span className="hub-info-col-dir">{t("general.DIRECTION")}</span>
              <span>{t("hub.routeColumn", { defaultValue: "Route" })}</span>
            </span>
          </div>

          {!filteredHubs.length && (
            <div className="hub-info-empty hub-info-empty--compact">
              {t("general.NO_RESULTS")}
            </div>
          )}

          {filteredHubs.map(hub => {
            const hubAreaId = hub.hubAreaId;
            const canClickArea = canOpenArea && Boolean(hubAreaId);

            return (
              <div key={hub.hubId} className="hub-info-row">
                <div className="hub-info-hub hub-info-col-hub">
                  {canOpenStation ? (
                    <button
                      type="button"
                      className="hub-info-hub-name hub-info-hub-name--clickable"
                      onClick={() => handleOpenStation(hub.hubId)}
                    >
                      {renderHubName(hub.hubName)}
                    </button>
                  ) : (
                    <span className="hub-info-hub-name">
                      {renderHubName(hub.hubName)}
                    </span>
                  )}
                  {hub.hubAreaName &&
                    (canClickArea && hubAreaId ? (
                      <button
                        type="button"
                        className="hub-info-hub-area hub-info-hub-area--clickable"
                        onClick={() => handleOpenArea(hubAreaId)}
                      >
                        {decorateWithSearch(hub.hubAreaName, searchValue)}
                      </button>
                    ) : (
                      <span className="hub-info-hub-area">
                        {decorateWithSearch(hub.hubAreaName, searchValue)}
                      </span>
                    ))}
                </div>
                <div className="hub-info-pairs hub-info-col-route">
                  {hub.pairs.map(pair => (
                    <div
                      key={`${pair.stationId}-${pair.pairedStationId}`}
                      className="hub-info-route"
                    >
                      <span
                        className="hub-info-direction-chip"
                        title={t(
                          `selectListLabel.Dir${pair.direction ?? DirectionEnum.Both}`,
                        )}
                      >
                        {formatHubSettingsDirectionLabel(pair.direction)}
                      </span>
                      <div className="hub-info-route-stations">
                        <StationBadge
                          name={pair.stationName}
                          areaName={pair.stationAreaName}
                          areaId={pair.stationAreaId}
                          stationId={pair.stationId}
                          canOpen={canOpenStation}
                          searchValue={searchValue}
                          onOpen={handleOpenStation}
                        />
                        <span className="hub-info-route-sep">-</span>
                        <StationBadge
                          name={hub.hubName}
                          areaName={hub.hubAreaName}
                          areaId={hub.hubAreaId}
                          stationId={hub.hubId}
                          isHub
                          canOpen={canOpenStation}
                          searchValue={searchValue}
                          onOpen={handleOpenStation}
                        />
                        <span className="hub-info-route-sep">-</span>
                        <StationBadge
                          name={pair.pairedStationName}
                          areaName={pair.pairedStationAreaName}
                          areaId={pair.pairedStationAreaId}
                          stationId={pair.pairedStationId}
                          canOpen={canOpenStation}
                          searchValue={searchValue}
                          onOpen={handleOpenStation}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </HubStationsInfoContentStyled>
  );
};
